import { Checkbox, Tooltip } from '@mui/material';
import React from 'react';
import { getIcon } from 'feature/document/definitions';
import { RoomSelect } from 'global/components/room-select';
import { Column, Row } from 'global/components/row';
import { Text } from 'global/components/text';
import { get, size } from 'global/lib/lodash';
import { LeadIconWrap, PreviewActionCheckbox } from './assignment-list.styles';

function dedupeRealmRooms(rooms) {
  const seen = new Set();
  return (rooms || []).filter((r) => {
    const id = String(r.id);
    if (seen.has(id)) {
      return false;
    }
    seen.add(id);
    return true;
  });
}

/** Stable key for comparing tag sets on edition items / checkpoints (realm or room ids). */
function roomsTagSetKey(rooms) {
  if (!size(rooms)) return '';
  return [...new Set((rooms || []).map((r) => String(r.id)))].sort().join(',');
}

/**
 * Header RoomSelect value and row `compare` for chip dimming. When rows disagree, header is []
 * and bulkCompare is null. When uniform, bulkCompare is the same array as the deduped header.
 */
export function uniformHeaderRoomsAndBulkCompare(items, getRooms) {
  if (!size(items)) {
    const empty = [];
    return [empty, empty];
  }
  const key0 = roomsTagSetKey(getRooms(items[0]) || []);
  for (let i = 1; i < items.length; i++) {
    if (roomsTagSetKey(getRooms(items[i]) || []) !== key0) {
      return [[], null];
    }
  }
  const rooms0 = getRooms(items[0]) || [];
  const header = size(rooms0) ? dedupeRealmRooms([...rooms0]) : [];
  return [header, header];
}

/** True when tagged realm ids are exactly the set of allRealms (same effective access as untagged). */
export function coversAllRealms(rooms, allRealms) {
  if (!size(rooms) || !size(allRealms)) {
    return false;
  }
  const roomIds = new Set((rooms || []).map((r) => String(r.id)));
  const allIds = new Set(allRealms.map((r) => String(r.id)));
  if (roomIds.size !== allIds.size) {
    return false;
  }
  for (const id of allIds) {
    if (!roomIds.has(id)) {
      return false;
    }
  }
  return true;
}

/** Only one division is tagged and it is this preview division — cannot clear via preview (would imply all-access). */
export function realmPreviewUncheckDisabledForSingletonTag(item, previewRealm) {
  if (!previewRealm) {
    return false;
  }
  const rooms = item.rooms || [];
  if (size(rooms) !== 1) {
    return false;
  }
  return String(rooms[0].id) === String(previewRealm.id);
}

/** Untagged ([]) or an explicit tag list covering every division = visible at all locations. */
export function isEffectivelyAllLocationsAccess(rooms, allRealms) {
  if (!size(rooms)) {
    return true;
  }
  return size(allRealms) > 0 && coversAllRealms(rooms, allRealms);
}

/** Room-assignments preview: toggle one room on a checkpoint's rooms list. */
export function computeNextCheckpointRoomsForPreview(checkpoint, previewRealm, checked) {
  const rid = String(previewRealm.id);
  const cur = checkpoint.rooms || [];
  if (checked) {
    if (cur.some((r) => String(r.id) === rid)) {
      return cur;
    }
    return [...cur, previewRealm];
  }
  return cur.filter((r) => String(r.id) !== rid);
}

/** Realm-assignments preview: untagged OR full explicit list = all locations selected; toggles match that model. */
export function computeNextDocumentItemRoomsForPreview(item, previewRealm, checked, allRealms) {
  const rid = String(previewRealm.id);
  const cur = Array.isArray(item.rooms) ? item.rooms : [];

  if (!size(allRealms)) {
    if (checked) {
      if (cur.some((r) => String(r.id) === rid)) {
        return cur;
      }
      return [...cur, { id: previewRealm.id, name: previewRealm.name }];
    }
    return cur.filter((r) => String(r.id) !== rid);
  }

  if (checked) {
    if (isEffectivelyAllLocationsAccess(cur, allRealms)) {
      return cur;
    }
    if (cur.some((r) => String(r.id) === rid)) {
      return cur;
    }
    const merged = dedupeRealmRooms([...cur, { id: previewRealm.id, name: previewRealm.name }]);
    return coversAllRealms(merged, allRealms) ? [] : merged;
  }

  if (isEffectivelyAllLocationsAccess(cur, allRealms)) {
    return allRealms.filter((r) => String(r.id) !== rid).map((r) => ({ id: r.id, name: r.name }));
  }
  return cur.filter((r) => String(r.id) !== rid);
}

/**
 * Checkbox "selected" for division preview: true if untagged (all locations), or full explicit list (equivalent),
 * or this division is explicitly in the tag list.
 */
export function isDocumentItemTaggedForRealm(item, previewRealm, allRealms = []) {
  const rooms = item.rooms || [];
  if (!size(rooms)) {
    return true;
  }
  if (size(allRealms) && coversAllRealms(rooms, allRealms)) {
    return true;
  }
  return rooms.some((r) => String(r.id) === String(previewRealm.id));
}

export function isCheckpointTaggedForRoom(checkpoint, previewRealm) {
  return (checkpoint.rooms || []).some((r) => String(r.id) === String(previewRealm.id));
}

function checklistHasRoomForLocationCopy(checklist, room) {
  return (checklist.rooms || []).some((r) => String(r.id) === String(room.id));
}

/** Mirror source room's checkpoint access onto target (same criteria tagged / not tagged). */
export function computeCheckpointRoomsAfterCopyLocationAccess(checkpoint, sourceRoom, targetRoom) {
  const sourceOn = isCheckpointTaggedForRoom(checkpoint, sourceRoom);
  const targetOn = isCheckpointTaggedForRoom(checkpoint, targetRoom);
  if (sourceOn === targetOn) {
    return checkpoint.rooms || [];
  }
  if (sourceOn) {
    return computeNextCheckpointRoomsForPreview(checkpoint, targetRoom, true);
  }
  return computeNextCheckpointRoomsForPreview(checkpoint, targetRoom, false);
}

export function computeChecklistBulkRoomsAfterCopyLocationAccess(checklist, sourceRoom, targetRoom) {
  const sourceOn = checklistHasRoomForLocationCopy(checklist, sourceRoom);
  const targetOn = checklistHasRoomForLocationCopy(checklist, targetRoom);
  if (sourceOn === targetOn) {
    return checklist.rooms || [];
  }
  const cur = checklist.rooms || [];
  const tid = String(targetRoom.id);
  if (sourceOn) {
    if (cur.some((r) => String(r.id) === tid)) {
      return cur;
    }
    return [...cur, targetRoom];
  }
  return cur.filter((r) => String(r.id) !== tid);
}

/** Mirror source division's document visibility onto target (uses same rules as preview toggles). */
export function computeItemRoomsAfterCopyLocationAccess(item, sourceRealm, targetRealm, allRealms) {
  const sourceOn = isDocumentItemTaggedForRealm(item, sourceRealm, allRealms);
  const targetOn = isDocumentItemTaggedForRealm(item, targetRealm, allRealms);
  if (sourceOn === targetOn) {
    return item.rooms || [];
  }
  if (sourceOn) {
    return computeNextDocumentItemRoomsForPreview(item, targetRealm, true, allRealms);
  }
  return computeNextDocumentItemRoomsForPreview(item, targetRealm, false, allRealms);
}

export function applyRoomLocationAccessFromSourceToTarget(checklists, sourceRoom, targetRoom) {
  if (!sourceRoom || !targetRoom || String(sourceRoom.id) === String(targetRoom.id)) {
    return checklists;
  }
  return checklists.map((cls) => ({
    ...cls,
    rooms: computeChecklistBulkRoomsAfterCopyLocationAccess(cls, sourceRoom, targetRoom),
    checkpoints: cls.checkpoints.map((cp) => ({
      ...cp,
      rooms: computeCheckpointRoomsAfterCopyLocationAccess(cp, sourceRoom, targetRoom),
    })),
  }));
}

export function applyDivisionLocationAccessFromSourceToTarget(docs, sourceRealm, targetRealm, allRealms) {
  if (!sourceRealm || !targetRealm || String(sourceRealm.id) === String(targetRealm.id)) {
    return docs;
  }
  return docs.map((doc) => ({
    ...doc,
    items: doc.items.map((item) => ({
      ...item,
      rooms: computeItemRoomsAfterCopyLocationAccess(item, sourceRealm, targetRealm, allRealms),
    })),
  }));
}

/** Library documentableType icon + document name + room selector or preview checkbox. */
export function AssignmentDocumentRow(props) {
  const {
    item,
    doc,
    rooms,
    updateChecks,
    onCopy,
    copiedRooms,
    isMobileXS,
    fixMargins,
    rowClassName,
    interaction = 'roomSelect',
    previewRealm,
    allRealms,
    bulkCompareRooms,
  } = props;
  const documentableType = get(item, 'documentableType', '');
  const CONT = isMobileXS ? Column : Row;

  const titleRow = (style = { cursor: 'pointer', minWidth: 200, maxWidth: isMobileXS ? undefined : '50%' }) => (
    <Row style={style} alignItems="center">
      <LeadIconWrap>{getIcon(documentableType, { fontSize: 'small' })}</LeadIconWrap>
      <Text type="label_large">{get(item, 'document.name')}</Text>
    </Row>
  );

  const realmPreviewChecked = interaction === 'previewCheckbox' && previewRealm ? isDocumentItemTaggedForRealm(item, previewRealm, allRealms) : false;
  const singletonUncheckLocked = interaction === 'previewCheckbox' && realmPreviewUncheckDisabledForSingletonTag(item, previewRealm);

  const onRealmPreviewChange = (checked) => {
    if (!previewRealm || !Array.isArray(allRealms)) {
      return;
    }
    const next = computeNextDocumentItemRoomsForPreview(item, previewRealm, checked, allRealms);
    updateChecks(doc, next, item, false);
  };

  const realmSingletonTooltip =
    'This document is assigned only to this division. To remove or change that access, use the location selector in the Document Location Assignments list.';

  const highlightBulk = bulkCompareRooms != null;

  return (
    <div className={rowClassName}>
      <CONT>
        {titleRow()}
        <div style={{ marginLeft: 'auto' }}>
          {interaction === 'previewCheckbox' ? (
            <Tooltip title={singletonUncheckLocked ? realmSingletonTooltip : ''} disableHoverListener={!singletonUncheckLocked}>
              <span style={{ display: 'inline-flex', alignItems: 'center', lineHeight: 0 }}>
                <PreviewActionCheckbox
                  size="small"
                  checked={realmPreviewChecked}
                  disabled={singletonUncheckLocked}
                  disableRipple
                  onChange={(e) => onRealmPreviewChange(e.target.checked)}
                  color="primary"
                />
              </span>
            </Tooltip>
          ) : (
            <Row style={{ alignItems: 'center' }}>
              {!size(item.rooms) && (
                <Text italic type="label_large" style={{ marginRight: 8 }}>
                  All Access
                </Text>
              )}
              <RoomSelect
                type="inlineModal"
                onCopy={onCopy}
                onPaste={copiedRooms ? () => copiedRooms : undefined}
                value={item.rooms || []}
                compare={highlightBulk ? bulkCompareRooms : item.rooms}
                rooms={rooms}
                onChange={(sel, areEqual) => updateChecks(doc, sel, item, areEqual)}
                modalTitle={titleRow(fixMargins)}
                highlightOriginals={highlightBulk}
              />
            </Row>
          )}
        </div>
      </CONT>
    </div>
  );
}

/** Leading disabled checkbox (checkpoint) + name & description + room selector or preview checkbox. */
function AssignmentCheckpointRowInner(props) {
  const {
    checkpoint,
    checklist,
    rooms,
    updateChecks,
    onCopy,
    copiedRooms,
    isMobileXS,
    fixMargins,
    rowClassName,
    interaction = 'roomSelect',
    previewRealm,
    bulkCompareRooms,
  } = props;
  const CONT = isMobileXS ? Column : Row;
  const description = get(checkpoint, 'description') || get(checkpoint, 'criteria') || '';

  const titleBlock = (
    style = {
      cursor: 'pointer',
      marginLeft: 0,
      minWidth: 200,
      maxWidth: isMobileXS ? undefined : '50%',
    }
  ) => (
    <Row style={style} alignItems="center">
      <Checkbox size="small" checked={false} disabled disableRipple sx={{ p: 0, mr: 0.5, flexShrink: 0 }} />
      <Text type="label_large" style={{ flex: 1, minWidth: 0 }}>
        {description ? (
          <>
            {checkpoint.name}
            <span>{': '}</span>
            {description}
          </>
        ) : (
          checkpoint.name
        )}
      </Text>
    </Row>
  );

  const roomPreviewChecked = interaction === 'previewCheckbox' && previewRealm ? isCheckpointTaggedForRoom(checkpoint, previewRealm) : false;

  const onRoomPreviewChange = (checked) => {
    if (!previewRealm) {
      return;
    }
    const next = computeNextCheckpointRoomsForPreview(checkpoint, previewRealm, checked);
    updateChecks(checklist, next, checkpoint, false);
  };

  const highlightBulk = bulkCompareRooms != null;

  return (
    <div className={rowClassName}>
      <CONT>
        {titleBlock()}
        <div style={{ marginLeft: 'auto' }}>
          {interaction === 'previewCheckbox' ? (
            <PreviewActionCheckbox
              size="small"
              checked={roomPreviewChecked}
              disableRipple
              onChange={(e) => onRoomPreviewChange(e.target.checked)}
              color="primary"
            />
          ) : (
            <RoomSelect
              type="inlineModal"
              onCopy={onCopy}
              onPaste={copiedRooms ? () => copiedRooms : undefined}
              value={checkpoint.rooms || []}
              compare={highlightBulk ? bulkCompareRooms : checkpoint.rooms}
              rooms={rooms}
              onChange={(sel, areEqual) => updateChecks(checklist, sel, checkpoint, areEqual)}
              modalTitle={titleBlock(fixMargins)}
              highlightOriginals={highlightBulk}
            />
          )}
        </div>
      </CONT>
    </div>
  );
}

function assignmentCheckpointRowPropsEqual(prev, next) {
  return (
    prev.checkpoint === next.checkpoint &&
    prev.interaction === next.interaction &&
    prev.previewRealm === next.previewRealm &&
    prev.updateChecks === next.updateChecks &&
    prev.rooms === next.rooms &&
    prev.onCopy === next.onCopy &&
    prev.copiedRooms === next.copiedRooms &&
    prev.isMobileXS === next.isMobileXS &&
    prev.rowClassName === next.rowClassName &&
    prev.fixMargins === next.fixMargins &&
    prev.bulkCompareRooms === next.bulkCompareRooms &&
    (prev.interaction === 'previewCheckbox' || prev.checklist === next.checklist)
  );
}

export const AssignmentCheckpointRow = React.memo(AssignmentCheckpointRowInner, assignmentCheckpointRowPropsEqual);
